// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// Functional core for CO-5(b): materialize the CLD as its OWN persisted view
// (Williams 2026-08-07 -- "one model, many views"). The model (variables, links,
// equations) is shared; each view arranges those elements for its own job. The
// SFD view (views[0]) is laid out for stock-flow reading; this builds the CLD
// view (views[1]) with the loop members arranged as rings, so the CLD stays a
// clean causal-loop diagram AND is a real, draggable, persisted canvas rather
// than a render-time overlay.
//
// It derives the CLD view from the SFD view so every node reuses a known-valid
// element (uid, type, var ref) -- we only move the loop members to their ring
// positions. It does NOT synthesize new links: a link from a stock to its own
// flow isn't valid XMILE, so adding one broke the download/re-open round-trip
// (duplicated variables) and cluttered the render. The balancing loops' "-"
// labels ride the real stock->aux connectors that already exist (e.g. Installed
// Base -> Prospects), and the loop-cycle outline shows each closure, so no
// synthetic edges are needed.

import { StockFlowView, ViewElement } from '@simlin/core/datamodel';

/** A plain 2D point (ring-layout output); NOT the datamodel Point, which also
 * carries an `attachedToUid`. */
interface XY {
  readonly x: number;
  readonly y: number;
}

/** Target closest-pair spacing for text-only CLD nodes (px). The engine lays out
 * for the compact SFD; the words need more room than that or they collide. */
export const CLD_MIN_NODE_SPACING = 150;

/** Options for {@link spreadOverlaps}. */
export interface SpreadOptions {
  /** Relaxation passes. Overlaps resolve within a handful; bounded for safety. */
  readonly maxIterations?: number;
}

/**
 * Push any pair of CLD nodes closer than `minSpacing` apart along their connecting
 * line, iterating until no pair overlaps (LOCAL de-crowd). Replaces the old global
 * scale: a single near-coincident pair used to force a huge scale factor that blew
 * a dense (12-loop) layout out to thousands of px, while leaving distant nodes over-
 * spread. This spreads only the crowded neighbourhoods, so the dense case stays
 * contained and the sparse case (Bass) still separates cleanly. Coincident nodes
 * get a deterministic jitter so they can part. Pure -- unit-tests without a canvas.
 */
export function spreadOverlaps(
  positions: ReadonlyMap<string, XY>,
  minSpacing: number,
  options: SpreadOptions = {},
): Map<string, XY> {
  const maxIterations = options.maxIterations ?? 200;
  const pts = [...positions.entries()].map(([id, p]) => ({ id, x: p.x, y: p.y }));
  if (pts.length < 2) {
    return new Map(positions);
  }
  for (let iter = 0; iter < maxIterations; iter++) {
    let moved = false;
    for (let i = 0; i < pts.length; i++) {
      for (let j = i + 1; j < pts.length; j++) {
        let dx = pts[j].x - pts[i].x;
        let dy = pts[j].y - pts[i].y;
        let d = Math.hypot(dx, dy);
        if (d < 1e-6) {
          // Coincident: jitter deterministically (golden-angle) so they separate.
          dx = Math.cos(i * 2.399963);
          dy = Math.sin(i * 2.399963);
          d = 1;
        }
        if (d < minSpacing) {
          const push = (minSpacing - d) / 2;
          const ux = dx / d;
          const uy = dy / d;
          pts[i].x -= ux * push;
          pts[i].y -= uy * push;
          pts[j].x += ux * push;
          pts[j].y += uy * push;
          moved = true;
        }
      }
    }
    if (!moved) {
      break;
    }
  }
  const out = new Map<string, XY>();
  for (const p of pts) {
    out.set(p.id, { x: p.x, y: p.y });
  }
  return out;
}

/**
 * Build the CLD view from the SFD view: loop members move to their
 * `ringPositions` (a flow's pipe points translate by the same delta so its
 * geometry stays consistent); every other element carries through unchanged (the
 * CLD render filters non-members out, but keeping them keeps every link's
 * from/to uid resolvable and the view valid for the engine round-trip).
 */
export function buildCldView(
  sfdView: StockFlowView,
  memberIdents: readonly string[],
  ringPositions: ReadonlyMap<string, XY>,
): StockFlowView {
  const memberSet = new Set(memberIdents);

  const elements: ViewElement[] = sfdView.elements.map((el) => {
    const ident = (el as { ident?: unknown }).ident;
    if (typeof ident !== 'string' || !memberSet.has(ident)) {
      return el;
    }
    const pos = ringPositions.get(ident);
    if (!pos) {
      return el;
    }
    if (el.type === 'flow') {
      const dx = pos.x - el.x;
      const dy = pos.y - el.y;
      return {
        ...el,
        x: pos.x,
        y: pos.y,
        // Preserve each point's other fields (e.g. attachedToUid); only translate.
        points: el.points.map((p) => ({ ...p, x: p.x + dx, y: p.y + dy })),
      };
    }
    return { ...el, x: pos.x, y: pos.y };
  });

  // Spread the source view so StockFlowView-only fields (viewBox, zoom,
  // useLetteredPolarity, ...) carry over; override just the elements.
  return {
    ...sfdView,
    elements,
  };
}
